import React, { useState, useEffect } from "react";
import "@/App.css";
import { 
  Zap, 
  BookOpen, 
  Brain, 
  Cpu, 
  Check, 
  ChevronDown, 
  ChevronUp, 
  Users, 
  Facebook, 
  Linkedin, 
  Star, 
  Sparkles, 
  Calculator, 
  Mail, 
  Phone, 
  User, 
  GraduationCap, 
  Award, 
  ShieldCheck, 
  Compass, 
  Heart,
  ExternalLink,
  ArrowRight,
  Info,
  Menu,
  X
} from "lucide-react";

// Image URLs provided in the system context
const LOGO_URL = "https://tagmango.com/assets/final-logo-neural-equation-copy-c886191f3b94f31be7f78a9ff4fe0bf5.png";
const VIJAY_PHOTO_URL = "https://tagmango.com/assets/vijay-dharmadhikari-c1b30d47fe2df6d4801b6544672222b5.png";
const MASTERY_CIRCLE_URL = "https://tagmango.com/assets/the-vedic-maths-mastery-circle-56123c533bd38fafc49267c1eda7233a.png";
const MASTERY_HUB_URL = "https://tagmango.com/assets/the-vedic-maths-mastery-hub-148314d4a159e7c30b33ccafc26802ad.png";

export default function App() {
  // Navigation State
  const [activeSection, setActiveSection] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Interactive "Book My Seat" Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState("L2 — SUTRA™ INFLUENCE (₹2499)");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    audienceType: "Parent",
  });
  const [formSuccess, setFormDataSuccess] = useState(false);
  const [submissionCode, setSubmissionCode] = useState("");

  // Toast State
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  // Pain Points Accordion State
  const [openPainPoint, setOpenPainPoint] = useState(null);

  // How This Helps Tabbed UI State
  const [activeTab, setActiveTab] = useState("students");

  // Under The Hood Interactive Solver State
  const [numA, setNumA] = useState("13");
  const [numB, setNumB] = useState("12");
  const [solverResult, setSolverResult] = useState(null);
  const [selectedSutraStep, setSelectedSutraStep] = useState("all");

  // Toast trigger helper
  const triggerToast = (msg) => {
    setToastMessage(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  // Pre-fill Solver demo upon load
  useEffect(() => {
    calculateVedicMultiplication("13", "12");
  }, []);

  // Escape key event listener to close modal (scoped to isModalOpen)
  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen]);

  // Simple Vedic Urdhva-Tiryagbhyam Solver logic for visual demonstration
  const calculateVedicMultiplication = (valA, valB) => {
    const a = parseInt(valA);
    const b = parseInt(valB);
    if (isNaN(a) || a < 10 || a > 99 || isNaN(b) || b < 10 || b > 99) {
      setSolverResult(null);
      return;
    }
    
    // Extracted digits:
    // a = a1 a0 (e.g. 1 3)
    // b = b1 b0 (e.g. 1 2)
    const a1 = Math.floor(a / 10);
    const a0 = a % 10;
    const b1 = Math.floor(b / 10);
    const b0 = b % 10;

    // Urdhva Tiryagbhyam steps:
    // Step 1: Right digits vertical multiplication (a0 * b0)
    const step1Val = a0 * b0;
    // Step 2: Crosswise multiplication and addition (a1 * b0 + a0 * b1)
    const step2Val = (a1 * b0) + (a0 * b1);
    // Step 3: Left digits vertical multiplication (a1 * b1)
    const step3Val = a1 * b1;

    // Calculation trace
    const finalAns = a * b;

    setSolverResult({
      a1, a0, b1, b0,
      step1: `${a0} × ${b0} = ${step1Val}`,
      step2: `(${a1} × ${b0}) + (${a0} × ${b1}) = ${step2Val}`,
      step3: `${a1} × ${b1} = ${step3Val}`,
      step1Val,
      step2Val,
      step3Val,
      finalAns,
      algebraProof: `(10x + ${a0})(10y + ${b0}) = 100xy + 10(x·${b0} + y·${a0}) + ${step1Val}`
    });
  };

  const handleSolverChange = (e, type) => {
    const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 2);
    if (type === "A") {
      setNumA(val);
      calculateVedicMultiplication(val, numB);
    } else {
      setNumB(val);
      calculateVedicMultiplication(numA, val);
    }
  };

  // Pain Points List
  const painPoints = [
    {
      id: 1,
      title: "Mathematics Anxiety and Phobia",
      problem: "Traditional methods focus on strict, rigid rules that punish errors and provoke intense academic anxiety in student minds.",
      solution: "The curriculum introduces the Vedic principle of coherence and flexibility. Vedic Mathematics demonstrates that a single Sutra can apply across multiple mathematical domains, showing that math is a playful puzzle."
    },
    {
      id: 2,
      title: "Severe Time Deficits in Examinations",
      problem: "Arithmetic calculations absorb 60% of students' total exam time, causing unfinished papers and missed score marks.",
      solution: "We introduce specific 'speed algorithms' derived from the Sutras, such as Urdhva-Tiryagbhyam (Vertically and Crosswise). This allows instantaneous single-line calculations of large multiplications, reducing math to a breeze."
    },
    {
      id: 3,
      title: "Lack of Autonomous Verification Mechanisms",
      problem: "Students must finish the entire page before knowing if they made a typo, forcing frustrating recalculations.",
      solution: "We natively integrate Vedic verification techniques, such as the 'Digit Sum' method (casting out nines). This allows verifying multiplication, division, and algebra results in under 5 seconds."
    },
    {
      id: 4,
      title: "The Disconnect Between Arithmetic and Algebra",
      problem: "Algebra feels like an abstract, frightening language separated from normal numbers.",
      solution: "Our digital modules explicitly link arithmetic shortcuts directly to algebraic polynomials. Students recognize that equations are just the general form of the numbers they work with daily."
    },
    {
      id: 5,
      title: "Rote Memorization Without Conceptual Grounding",
      problem: "Children memorize formulas mechanically, leaving them completely lost when questions are slightly twisted.",
      solution: "Every module features an 'Under the Hood' segment where we reveal the algebraic proof of the Sutra. Students learn the logic behind the fast-math, building true conceptual mastery."
    },
    {
      id: 6,
      title: "Attention Attrition in Digital Learning",
      problem: "Standard online math lessons are boring, causing screen fatigue and rapid drop-offs in focus.",
      solution: "We employ gamified narrative instructional designs—framing mathematical Sutras as ancient 'cognitive algorithms' that students unlock on a magical quest."
    },
    {
      id: 7,
      title: "Financial Barriers to Premium Education",
      problem: "High-quality academic training is often reserved for elite families who can afford hefty coaching fees.",
      solution: "We implement a strict cross-subsidization economic model, making foundational L0 courses globally affordable at just ₹499 while offering elite certifications."
    },
    {
      id: 8,
      title: "Curriculum Friction and School Interference",
      problem: "Schools mandate standard math steps, meaning students get penalized if they only write down direct shortcuts.",
      solution: "We design the curriculum to be entirely complementary. Students use standard methods to write down their working steps, and apply Vedic speed-math as an invisible, lightning-fast background double-check to ensure absolute correctness."
    },
    {
      id: 9,
      title: "Lack of Peer and Community Engagement",
      problem: "Math studies are highly isolating, leading to early giving up and loss of momentum.",
      solution: "Our students enter 'The Vedic Maths Mastery Circle'—gaining access to math olympiad digital tournaments, collaborative problem-solving quests, and direct peer peer mentoring."
    },
    {
      id: 10,
      title: "Inconsistent Instructor Quality at Scale",
      problem: "Most tutoring centers rely on low-paid, poorly certified instructors with zero pedagogical framework.",
      solution: "We've built a rigorous 'Train the Trainer' (TTT) pipeline. Every facilitator is extensively certified in child cognitive psychology and adult learning theories before instructing classrooms."
    }
  ];

  // Membership Tiers
  const membershipTiers = [
    {
      id: "l0",
      name: "L0 — SUTRA IDENTITY",
      tagline: "Your Journey Grows With You",
      originalPrice: "₹999",
      price: "499",
      badge: "Perfect Foundation",
      features: [
        "Introduction to Vedic Mathematics",
        "Foundations of Vedic Mathematics",
        "Problem Solving Skills",
        "Speed Maths Principles",
        "Winning Promise Formula",
        "Pattern Recognition Blueprint"
      ],
      color: "border-slate-700/50"
    },
    {
      id: "l1",
      name: "L1 — SUTRA™ ALIGNMENT",
      tagline: "SILVER MEMBERSHIP",
      originalPrice: "₹2,499",
      price: "999",
      badge: "Foundational Speed",
      features: [
        "Silver 3 Days Course access",
        "Bonus 1: Foundations of Speed",
        "Bonus 2: Clarity Kickstart Blueprint",
        "Bonus 3: Confidence Starter Pack",
        "Bonus 4: Memory Basics Framework",
        "Bonus 5: Reward Engine Formula",
        "Bonus 6: Growth Loop System"
      ],
      color: "border-blue-500/20"
    },
    {
      id: "l2",
      name: "L2 — SUTRA™ INFLUENCE",
      tagline: "GOLD MEMBERSHIP",
      originalPrice: "₹7,999",
      price: "2499",
      badge: "Most Popular",
      features: [
        "Gold 3 Days Premium Course",
        "Bonus 1: Speed Calculation Secrets",
        "Bonus 2: Clarity Formula Blueprint",
        "Bonus 3: Confidence Multiplier System",
        "Bonus 4: Memory Mastery Framework",
        "Bonus 5: Cognitive Boost Engine",
        "Bonus 6: Competitive Edge System"
      ],
      color: "border-amber-500 shadow-[0_0_20px_rgba(212,175,55,0.2)]"
    },
    {
      id: "l3",
      name: "L3 — SUTRA™ WISDOM™",
      tagline: "DIAMOND MEMBERSHIP",
      originalPrice: "₹19,999",
      price: "4999",
      badge: "Elite Mastery & Teaching Rights",
      features: [
        "Diamond 3 Days Elite Course",
        "Bonus 1: Algebraic Insights Engine",
        "Bonus 2: Geometric Precision Framework",
        "Bonus 3: Cognitive Acceleration Protocol",
        "Bonus 4: Memory Matrix Integration",
        "Bonus 5: Strategic Problem Solving Compass",
        "Bonus 6: Competitive Edge Catalyst"
      ],
      color: "border-purple-500/30"
    }
  ];

  // Lead Form Submission Handler
  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.phone) {
      triggerToast("Please fill in all details to reserve your seat.");
      return;
    }

    // Save lead detail locally in localStorage
    const existingLeads = JSON.parse(localStorage.getItem("vedic_maths_leads") || "[]");
    const newLead = {
      ...formData,
      selectedTier,
      timestamp: new Date().toISOString(),
      id: Math.random().toString(36).substr(2, 9).toUpperCase()
    };
    existingLeads.push(newLead);
    localStorage.setItem("vedic_maths_leads", JSON.stringify(existingLeads));

    // Generate simulated booking registration code
    setSubmissionCode(`VMMC-${Math.floor(100000 + Math.random() * 900000)}`);
    setFormDataSuccess(true);
    triggerToast("✨ Seat reserved successfully! Check your email for confirmation details.");
  };

  // Scroll to section helper
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(id);
      setIsMobileMenuOpen(false);
    }
  };

  const openBookingModalWithTier = (tierName) => {
    setSelectedTier(tierName);
    // Reset form fields on open so they are clean and fresh
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      audienceType: "Parent",
    });
    setFormDataSuccess(false);
    setIsModalOpen(true);
  };

  // Handle overlay click to close modal
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setIsModalOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B132B] text-[#F8FAFC] font-sans antialiased selection:bg-[#D4AF37] selection:text-[#0B132B]">
      
      {/* Decorative Floating Symbols Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0 opacity-15">
        <div className="absolute top-[20%] left-[10%] text-6xl font-equation floating-symbol text-amber-500/20">√</div>
        <div className="absolute top-[35%] right-[15%] text-7xl font-equation floating-symbol text-amber-500/25" style={{ animationDelay: "2s" }}>∑</div>
        <div className="absolute top-[60%] left-[8%] text-8xl font-equation floating-symbol text-amber-500/20" style={{ animationDelay: "4s" }}>∞</div>
        <div className="absolute top-[75%] right-[10%] text-6xl font-equation floating-symbol text-amber-500/30" style={{ animationDelay: "1s" }}>π</div>
        <div className="absolute top-[45%] left-[45%] text-5xl font-equation floating-symbol text-amber-500/15" style={{ animationDelay: "5s" }}>∫</div>
        <div className="absolute top-[90%] left-[20%] text-7xl font-equation floating-symbol text-amber-500/20" style={{ animationDelay: "3s" }}>x² + y²</div>
      </div>

      {/* STICKY GLASSMORPHIC HEADER */}
      <header className="sticky top-0 z-40 w-full bg-[#0B132B]/85 backdrop-blur-md border-b border-[#D4AF37]/20 transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
          
          {/* Logo Brand Brand */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img 
              src={LOGO_URL} 
              alt="The Vedic Maths Mastery Hub Logo" 
              className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
              data-testid="header-logo-image"
            />
            <div className="flex flex-col text-left">
              <span className="font-display text-[#D4AF37] font-bold text-sm sm:text-base tracking-wider leading-none">
                THE VEDIC MATHS
              </span>
              <span className="text-[10px] sm:text-xs text-slate-300 font-mono tracking-widest leading-none mt-1">
                MASTERY HUB
              </span>
            </div>
          </div>

          {/* Desktop Nav Items */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection("sutra-path")} 
              className={`text-sm tracking-wide transition-colors hover:text-[#D4AF37] ${activeSection === "sutra-path" ? "text-[#D4AF37] font-semibold" : "text-slate-300"}`}
              data-testid="nav-link-sutra-path"
            >
              SUTRA Path
            </button>
            <button 
              onClick={() => scrollToSection("pillars")} 
              className={`text-sm tracking-wide transition-colors hover:text-[#D4AF37] ${activeSection === "pillars" ? "text-[#D4AF37] font-semibold" : "text-slate-300"}`}
              data-testid="nav-link-pillars"
            >
              Strategic Pillars
            </button>
            <button 
              onClick={() => scrollToSection("benefits")} 
              className={`text-sm tracking-wide transition-colors hover:text-[#D4AF37] ${activeSection === "benefits" ? "text-[#D4AF37] font-semibold" : "text-slate-300"}`}
              data-testid="nav-link-benefits"
            >
              Benefits
            </button>
            <button 
              onClick={() => scrollToSection("pain-points")} 
              className={`text-sm tracking-wide transition-colors hover:text-[#D4AF37] ${activeSection === "pain-points" ? "text-[#D4AF37] font-semibold" : "text-slate-300"}`}
              data-testid="nav-link-pain-points"
            >
              Pain Solutions
            </button>
            <button 
              onClick={() => scrollToSection("curriculum")} 
              className={`text-sm tracking-wide transition-colors hover:text-[#D4AF37] ${activeSection === "curriculum" ? "text-[#D4AF37] font-semibold" : "text-slate-300"}`}
              data-testid="nav-link-curriculum"
            >
              Curriculum
            </button>
            <button 
              onClick={() => scrollToSection("mentor")} 
              className={`text-sm tracking-wide transition-colors hover:text-[#D4AF37] ${activeSection === "mentor" ? "text-[#D4AF37] font-semibold" : "text-slate-300"}`}
              data-testid="nav-link-mentor"
            >
              Founder
            </button>
          </nav>

          {/* Call to Action Header Button */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => openBookingModalWithTier("L2 — SUTRA™ INFLUENCE (₹2499)")}
              className="bg-[#D4AF37] hover:bg-[#FFD700] text-[#0B132B] font-semibold text-xs sm:text-sm px-3 sm:px-6 py-2 sm:py-2.5 rounded-full transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-[0_4px_15px_rgba(212,175,55,0.3)] font-sans flex items-center space-x-1.5 whitespace-nowrap"
              data-testid="nav-book-seat-btn"
            >
              <span>Book My Seat</span>
              <Sparkles className="h-3.5 w-3.5" />
            </button>

            {/* Mobile Hamburger toggle button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#D4AF37] hover:text-[#FFD700] focus:outline-none focus:ring-0 ml-1"
              aria-label="Toggle navigation menu"
              data-testid="mobile-hamburger-toggle"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

        </div>

        {/* Mobile Navigation Dropdown Drawer */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-[#151E3D] border-b border-[#D4AF37]/20 py-4 px-6 animate-in slide-in-from-top duration-200" data-testid="mobile-nav-drawer">
            <div className="flex flex-col space-y-4 text-left">
              <button 
                onClick={() => scrollToSection("sutra-path")} 
                className="text-slate-200 hover:text-[#D4AF37] text-sm py-1 border-b border-slate-800 font-medium"
                data-testid="mobile-nav-link-sutra"
              >
                SUTRA Path
              </button>
              <button 
                onClick={() => scrollToSection("pillars")} 
                className="text-slate-200 hover:text-[#D4AF37] text-sm py-1 border-b border-slate-800 font-medium"
                data-testid="mobile-nav-link-pillars"
              >
                Strategic Pillars
              </button>
              <button 
                onClick={() => scrollToSection("benefits")} 
                className="text-slate-200 hover:text-[#D4AF37] text-sm py-1 border-b border-slate-800 font-medium"
                data-testid="mobile-nav-link-benefits"
              >
                Benefits
              </button>
              <button 
                onClick={() => scrollToSection("pain-points")} 
                className="text-slate-200 hover:text-[#D4AF37] text-sm py-1 border-b border-slate-800 font-medium"
                data-testid="mobile-nav-link-pain-points"
              >
                Pain Solutions
              </button>
              <button 
                onClick={() => scrollToSection("curriculum")} 
                className="text-slate-200 hover:text-[#D4AF37] text-sm py-1 border-b border-slate-800 font-medium"
                data-testid="mobile-nav-link-curriculum"
              >
                Curriculum
              </button>
              <button 
                onClick={() => scrollToSection("mentor")} 
                className="text-slate-200 hover:text-[#D4AF37] text-sm py-1 font-medium"
                data-testid="mobile-nav-link-mentor"
              >
                Founder
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="relative z-10">

        {/* HERO SECTION */}
        <section className="relative pt-12 pb-20 sm:pb-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Narrative Text Column */}
            <div className="lg:col-span-7 space-y-8 text-left">
              
              {/* Aligned Badge */}
              <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-3.5 py-1.5 rounded-full" data-testid="hero-iks-badge">
                <GraduationCap className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-xs font-mono tracking-widest text-[#D4AF37] font-semibold uppercase">
                  Indian Knowledge System (IKS) • NEP 2020 Aligned
                </span>
              </div>

              {/* Massive Main Heading */}
              <div className="space-y-4">
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-100 leading-tight">
                  Unlock <span className="bg-gradient-to-r from-amber-200 via-[#D4AF37] to-amber-500 bg-clip-text text-transparent">15X Calculation Speed</span> & Drop Math Anxiety Forever
                </h1>
                
                <p className="text-slate-300 text-base sm:text-lg lg:text-xl font-normal max-w-2xl leading-relaxed">
                  Transform math from a frightening barrier into a beautiful, smiling art. Aligned with the National Education Policy (NEP) 2020, we empower students, parents, and certified teachers through 16 timeless algebraic Sutras.
                </p>
              </div>

              {/* Quick Value Proof Metrics Row */}
              <div className="grid grid-cols-3 gap-4 border-y border-slate-700/40 py-6 my-4">
                <div className="text-left" data-testid="hero-stat-speed">
                  <div className="text-2xl sm:text-3xl font-bold text-[#D4AF37] font-equation flex items-center">
                    <span>15X</span>
                    <Zap className="h-4.5 w-4.5 text-yellow-400 ml-1 fill-yellow-400" />
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400 font-sans mt-1">Faster Math Solving</div>
                </div>
                <div className="text-left" data-testid="hero-stat-accuracy">
                  <div className="text-2xl sm:text-3xl font-bold text-[#D4AF37] font-equation flex items-center">
                    <span>100%</span>
                    <Award className="h-4.5 w-4.5 text-yellow-400 ml-1" />
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400 font-sans mt-1">Answer Verification</div>
                </div>
                <div className="text-left" data-testid="hero-stat-students">
                  <div className="text-2xl sm:text-3xl font-bold text-[#D4AF37] font-equation flex items-center">
                    <span>10K+</span>
                    <Users className="h-4.5 w-4.5 text-yellow-400 ml-1" />
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400 font-sans mt-1">Minds Transformed</div>
                </div>
              </div>

              {/* CTA Buttons Row */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <button
                  onClick={() => openBookingModalWithTier("L2 — SUTRA™ INFLUENCE (₹2499)")}
                  className="bg-[#D4AF37] hover:bg-[#FFD700] text-[#0B132B] text-base font-bold px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-[0_5px_25px_rgba(212,175,55,0.4)] flex items-center justify-center space-x-2"
                  data-testid="hero-cta-book-seat"
                >
                  <span>Book My Seat Now</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
                <button
                  onClick={() => scrollToSection("sutra-path")}
                  className="border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/5 text-base font-semibold px-8 py-4 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2"
                  data-testid="hero-cta-explore-sutra"
                >
                  <span>Discover SUTRA™ Path</span>
                </button>
              </div>

              {/* Founder Tagline Note */}
              <div className="flex items-center space-x-2.5 text-xs text-slate-400">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping"></span>
                <span>Led by Vijay Kumar Dharmadhikari • 35+ Years Rigorous Math Leadership</span>
              </div>

            </div>

            {/* Right Interactive Image Frame Column */}
            <div className="lg:col-span-5 relative mt-6 lg:mt-0 flex flex-col items-center justify-center">
              <div className="relative w-full max-w-[450px] space-y-5">
                
                {/* Visual Accent glow */}
                <div className="absolute -inset-1.5 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-3xl blur opacity-20"></div>
                
                {/* Hero Illustration Box (Thumbnail only) */}
                <div className="relative bg-[#151E3D] rounded-3xl p-3 sm:p-4 border border-[#D4AF37]/25 shadow-2xl overflow-hidden">
                  <img 
                    src={MASTERY_HUB_URL} 
                    alt="The Vedic Maths Mastery Hub" 
                    className="w-full rounded-2xl object-contain h-auto shadow-md"
                    data-testid="hero-banner-image"
                  />
                </div>

                {/* Brand Manifesto Box (Separated) */}
                <div className="relative bg-[#151E3D]/85 border border-[#D4AF37]/20 rounded-2xl p-4 sm:p-5 text-left shadow-xl overflow-hidden group">
                  <div className="absolute -right-8 -bottom-8 w-24 h-24 bg-[#D4AF37]/5 rounded-full blur-xl group-hover:bg-[#D4AF37]/10 transition-all duration-500"></div>
                  <p className="text-[#D4AF37] font-display text-xs sm:text-sm font-bold tracking-widest uppercase flex items-center gap-1.5">
                    <Sparkles className="h-4 w-4 text-[#D4AF37] animate-pulse" />
                    <span>Our Manifesto</span>
                  </p>
                  <p className="text-slate-300 text-xs sm:text-sm italic leading-relaxed mt-2 relative z-10">
                    &ldquo;Mathematics is not just about dry numbers—it is about unlocking human potential. We bridge timeless heritage with digital learning to spark joy, precision, and lightning-fast calculations.&rdquo;
                  </p>
                </div>

              </div>
            </div>

          </div>
        </section>


        {/* BRAND STATEMENT & PHILOSOPHY */}
        <section className="bg-[#151E3D]/50 border-y border-slate-800 py-16 px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#D4AF37]">
              The Core Vision
            </div>
            <h2 className="font-display text-2xl sm:text-4xl text-slate-100 font-bold leading-tight">
              &ldquo;Transforming Your Dream to Reality&rdquo;
            </h2>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-sans max-w-3xl mx-auto">
              The Vedic Maths Mastery Hub is your trusted framework for unlocking the genius within through the timeless power of ancient wisdom. At the heart of our journey lies the <strong>Vedic Maths Mastery Circle</strong> — a vibrant community where students, parents, educators, and math enthusiasts collaborate, share breakdowns, and celebrate intellectual growth.
            </p>
            <div className="h-0.5 w-16 bg-[#D4AF37] mx-auto mt-6"></div>
          </div>
        </section>


        {/* THE SUTRA PATH SECTION */}
        <section id="sutra-path" className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#D4AF37] font-semibold bg-[#D4AF37]/10 px-3 py-1 rounded-full">
              SUTRA Path™ Framework
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-100">
              The Threads of Mathematical Wisdom
            </h2>
            <p className="text-slate-300 text-base sm:text-lg">
              A philosophically aligned path directly tied to original Sanskrit texts, unlocking cognitive precision for modern digital success.
            </p>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 sm:gap-4 lg:gap-6">
            {[
              { letter: "S", title: "Speed", desc: "Calculate faster, think sharper, and stay ahead in tests.", color: "from-amber-500/20 to-yellow-500/5" },
              { letter: "U", title: "Understanding", desc: "Build deep structural math understanding, not mechanical tricks.", color: "from-amber-400/20 to-yellow-500/5" },
              { letter: "T", title: "Transformation", desc: "Transform deep-seated fear into high-flying academic confidence.", color: "from-amber-500/20 to-yellow-500/5" },
              { letter: "R", title: "Resilience", desc: "Develop a fearless mindset to tackle complex mathematical equations.", color: "from-amber-400/20 to-yellow-500/5" },
              { letter: "A", title: "Accuracy", desc: "Achieve error-free calculations through immediate verification.", color: "from-amber-500/20 to-yellow-500/5" }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#151E3D] border border-[#D4AF37]/20 rounded-2xl p-6 text-left hover:border-[#D4AF37]/60 transition-all duration-300 group hover:-translate-y-1 shadow-lg"
                data-testid={`sutra-card-${item.title.toLowerCase()}`}
              >
                <div className="font-display text-4xl sm:text-5xl font-black text-[#D4AF37] mb-4 group-hover:scale-110 transition-transform origin-left">
                  {item.letter}
                </div>
                <h3 className="text-lg font-bold text-slate-100 font-sans mb-2 tracking-wide">
                  {item.title}
                </h3>
                <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* SUTRA Promise Bar */}
          <div className="bg-[#151E3D]/60 border border-[#D4AF37]/30 rounded-2xl p-6 sm:p-8 mt-12 text-center max-w-4xl mx-auto">
            <p className="text-slate-200 text-sm sm:text-base italic leading-relaxed">
              &ldquo;Through SUTRA, we empower learners with Speed, Understanding, Transformation, Resilience, and Accuracy, unlocking the timeless wisdom of Vedic Mathematics for modern competitive success.&rdquo;
            </p>
          </div>
        </section>


        {/* THE 4 STRATEGIC PILLARS */}
        <section id="pillars" className="py-20 sm:py-28 bg-[#151E3D]/30 border-y border-slate-800/80 px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#D4AF37]">
                Cognitive Enhancement Pillars
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-100">
                The 4 Pillars of The Mastery Hub
              </h2>
              <p className="text-slate-300 text-base sm:text-lg">
                Not a temporary trick replacement, but a mental exoskeleton—a parallel operating system that optimizes neural agility.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Pillar 1 */}
              <div className="bg-[#151E3D] border border-slate-700/60 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/40 transition-all text-left" data-testid="pillar-card-synthesis">
                <div className="h-12 w-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-6">
                  <Brain className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-100 mb-3">
                  1. The Genius of Synthesis
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  We acknowledge Swami Bharati Krishna Tirthaji as the mathematical prodigy who synthesized universal truths into 16 Sutras and 13 Sub-Sutras. This system simplifies complex arithmetic into unified formulas that optimize cognitive processing.
                </p>
              </div>

              {/* Pillar 2 */}
              <div className="bg-[#151E3D] border border-slate-700/60 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/40 transition-all text-left" data-testid="pillar-card-architecture">
                <div className="h-12 w-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-6">
                  <Cpu className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-100 mb-3">
                  2. Architecture of the Mind
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  We position the framework as a mental enhancement program. Vedic Math works alongside standard curricula as an invisible operating system, boosting logical sequencing, working memory, and neural processing speeds.
                </p>
              </div>

              {/* Pillar 3 */}
              <div className="bg-[#151E3D] border border-slate-700/60 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/40 transition-all text-left" data-testid="pillar-card-proof">
                <div className="h-12 w-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-6">
                  <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-100 mb-3">
                  3. Proof over Promise (The Masterpiece Standard)
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  We answer academic skepticism head-on by proving that every single Vedic fast-math shortcut is actually a highly compressed algebraic theorem, encouraging kids to ask &ldquo;why&rdquo; and &ldquo;how&rdquo; rather than doing blind memorization.
                </p>
              </div>

              {/* Pillar 4 */}
              <div className="bg-[#151E3D] border border-slate-700/60 rounded-2xl p-6 sm:p-8 hover:border-[#D4AF37]/40 transition-all text-left" data-testid="pillar-card-utility">
                <div className="h-12 w-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-6">
                  <Compass className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-100 mb-3">
                  4. Universal Utility
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  For the Indian market, we align with the NEP 2020 ethos of reviving ancient sciences in a scientific, rational, and multidisciplinary manner. For the global market, we strip away barriers and deliver it as a highly optimized mental system for the digital age.
                </p>
              </div>

            </div>
          </div>
        </section>


        {/* HOW THIS HELPS YOU - TABBED UI */}
        <section id="benefits" className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#D4AF37]">
              Tailored Impact
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-100">
              How This Helps You all
            </h2>
            <p className="text-slate-300 text-base sm:text-lg">
              We empower learners across demographics to gain absolute speed, clarity, and precision in numbers.
            </p>
          </div>

          {/* Tab Selection Row */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-12 border-b border-slate-700/50 pb-4 max-w-3xl mx-auto">
            {[
              { id: "students", label: "Students (Classes 4-12)", icon: GraduationCap },
              { id: "parents", label: "Parents", icon: Heart },
              { id: "teachers", label: "Teachers & Educators", icon: Users },
              { id: "entrepreneurs", label: "Entrepreneurs & Enthusiasts", icon: Zap }
            ].map((tab) => {
              const Icon = tab.icon;
              const isSelected = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm sm:text-base transition-all duration-200 border ${
                    isSelected 
                      ? "bg-[#D4AF37] text-[#0B132B] font-bold border-[#D4AF37] shadow-[0_4px_12px_rgba(212,175,55,0.2)]" 
                      : "bg-[#151E3D] text-slate-300 hover:text-[#D4AF37] border-slate-700/60"
                  }`}
                  data-testid={`benefit-tab-trigger-${tab.id}`}
                >
                  <Icon className="h-4.5 w-4.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Content Cards */}
          <div className="max-w-4xl mx-auto bg-[#151E3D] border border-slate-700/80 rounded-2xl p-6 sm:p-10 shadow-xl text-left min-h-[220px]">
            {activeTab === "students" && (
              <div className="space-y-4 animate-in fade-in duration-300" data-testid="benefit-content-students">
                <h3 className="text-xl sm:text-2xl font-bold text-[#D4AF37] font-display flex items-center space-x-2">
                  <span>Conquer Fast Calculations & Overcome Fear</span>
                </h3>
                <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                  Overcome standard math anxiety and transform arithmetic into an enjoyable, rapid game like a puzzle or quest. Gain highly competitive, lightning-fast computations for high school and competitive entrance papers.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-400 text-sm mt-4">
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Fast calculations in single lines</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Complete competitive exams on time</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Immediate answer cross-checks</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Shift from math dread to deep interest</li>
                </ul>
              </div>
            )}

            {activeTab === "parents" && (
              <div className="space-y-4 animate-in fade-in duration-300" data-testid="benefit-content-parents">
                <h3 className="text-xl sm:text-2xl font-bold text-[#D4AF37] font-display flex items-center space-x-2">
                  <span>Watch Your Child Gain Unstoppable Agility</span>
                </h3>
                <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                  Parents gain profound clarity, confidence, and reassurance. Watch your kids build autonomous calculation accuracy, develop conceptual agility, and bypass dry rote-learning techniques.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-400 text-sm mt-4">
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Reduced reliance on external calculators</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Visible, measurable academic success</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Builds extreme focus & memory retention</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Secure cognitive foundation early</li>
                </ul>
              </div>
            )}

            {activeTab === "teachers" && (
              <div className="space-y-4 animate-in fade-in duration-300" data-testid="benefit-content-teachers">
                <h3 className="text-xl sm:text-2xl font-bold text-[#D4AF37] font-display flex items-center space-x-2">
                  <span>Incorporate Modern NEP-Aligned Pedagogy</span>
                </h3>
                <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                  Gain unique interactive tools to make traditional math classrooms engaging. Seamlessly integrate the Indian Knowledge Systems (IKS) mandate of NEP 2020 with high-quality teaching mechanisms that students love.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-400 text-sm mt-4">
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Standardized trainer certification pipeline</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Experiential curriculum delivery kits</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Gamify formulas for instant engagement</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> High-value career upward path</li>
                </ul>
              </div>
            )}

            {activeTab === "entrepreneurs" && (
              <div className="space-y-4 animate-in fade-in duration-300" data-testid="benefit-content-entrepreneurs">
                <h3 className="text-xl sm:text-2xl font-bold text-[#D4AF37] font-display flex items-center space-x-2">
                  <span>Develop Elite Mental Acuity & Business Agility</span>
                </h3>
                <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                  Entrepreneurs and solopreneurs discover how to gamify customer and student learning journeys to skyrocket engagement. Enthusiasts and adult learners experience calculations as a playful, confidence-boosting mental exercise.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-400 text-sm mt-4">
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Boost decision speed and agility</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Gamification triggers that double retention</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Scale certified ed-tech coaching models</li>
                  <li className="flex items-start"><Check className="h-4 w-4 text-emerald-400 mr-2 mt-1 shrink-0" /> Playful daily cognitive gym exercises</li>
                </ul>
              </div>
            )}
          </div>
        </section>


        {/* INTERACTIVE SOLVER DEMO - PROOF OVER PROMISE */}
        <section className="py-20 sm:py-24 bg-[#151E3D]/50 border-y border-slate-800/80 px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-5xl mx-auto">
            
            <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#D4AF37]">
                Experience Under the Hood Proofs
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-100">
                Interactive Vedic Calculation Engine
              </h2>
              <p className="text-slate-300 text-sm sm:text-base">
                Try the Urdhva-Tiryagbhyam (&ldquo;Vertically and Crosswise&rdquo;) formula below. Watch algebraic proofs power swift calculations.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-[#0B132B] border border-[#D4AF37]/25 rounded-3xl p-6 sm:p-8 shadow-2xl">
              
              {/* Controls Column */}
              <div className="lg:col-span-5 space-y-6 text-left">
                <h3 className="font-display text-lg sm:text-xl font-bold text-[#D4AF37]">
                  Input 2-Digit Numbers
                </h3>
                <p className="text-slate-400 text-xs sm:text-sm">
                  Type any double-digit numbers below (10 to 99) to visually decode how Vedic calculations resolve in a single horizontal line.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs text-slate-400 font-mono">Number A</label>
                    <input 
                      type="text"
                      value={numA}
                      onChange={(e) => handleSolverChange(e, "A")}
                      className="w-full bg-[#151E3D] border border-slate-700 focus:border-[#D4AF37] text-[#D4AF37] font-equation text-center text-2xl font-bold py-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                      placeholder="e.g. 13"
                      data-testid="vedic-solver-input-a"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-slate-400 font-mono">Number B</label>
                    <input 
                      type="text"
                      value={numB}
                      onChange={(e) => handleSolverChange(e, "B")}
                      className="w-full bg-[#151E3D] border border-slate-700 focus:border-[#D4AF37] text-[#D4AF37] font-equation text-center text-2xl font-bold py-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                      placeholder="e.g. 12"
                      data-testid="vedic-solver-input-b"
                    />
                  </div>
                </div>

                <div className="bg-[#151E3D] border border-slate-800 rounded-xl p-4 space-y-2">
                  <div className="text-xs font-mono text-[#D4AF37]">SUTRA FORMULA APPLIED:</div>
                  <div className="text-sm font-bold text-slate-200">Urdhva-Tiryagbhyam</div>
                  <div className="text-xs text-slate-400 leading-relaxed">&ldquo;Multiply vertically on extremities, cross-multiply in center, and add up carrying values.&rdquo;</div>
                </div>
              </div>

              {/* Visualization Column */}
              <div className="lg:col-span-7 bg-[#151E3D] rounded-2xl p-5 sm:p-6 border border-slate-700/60 text-left">
                {solverResult ? (
                  <div className="space-y-5 animate-in fade-in duration-200">
                    
                    {/* Sum visualization banner */}
                    <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                      <span className="text-xs font-mono text-slate-400">Calculation Diagram:</span>
                      <span className="text-xs font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">Verified</span>
                    </div>

                    {/* Sutra Animation Styles */}
                    <style dangerouslySetInnerHTML={{__html: `
                      @keyframes drawSutraPath {
                        from {
                          stroke-dashoffset: 150;
                        }
                        to {
                          stroke-dashoffset: 0;
                        }
                      }
                      .sutra-line-animate {
                        stroke-dasharray: 150;
                        stroke-dashoffset: 150;
                        animation: drawSutraPath 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
                      }
                    `}} />

                    {/* Interactive Animated SVG Diagram */}
                    <div className="bg-[#0B132B] border border-slate-800 rounded-xl p-4 sm:p-5 flex flex-col items-center space-y-4 relative overflow-hidden" data-testid="sutra-animation-container">
                      {/* Star dust background accent */}
                      <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#D4AF37_1px,transparent_1px)] [background-size:14px_16px]"></div>
                      
                      {/* Interactive Step Selector Tabs */}
                      <div className="flex flex-wrap gap-1 sm:gap-2 justify-center z-10 w-full border-b border-slate-800 pb-3">
                        {[
                          { id: "all", label: "Show All" },
                          { id: "step1", label: "Step 1 (Right Col)" },
                          { id: "step2", label: "Step 2 (Crosswise)" },
                          { id: "step3", label: "Step 3 (Left Col)" }
                        ].map((step) => (
                          <button
                            key={step.id}
                            onClick={() => setSelectedSutraStep(step.id)}
                            data-testid={`sutra-step-tab-${step.id}`}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                              selectedSutraStep === step.id
                                ? "bg-[#D4AF37] text-[#0B132B] shadow-md shadow-[#D4AF37]/25 font-bold"
                                : "bg-[#151E3D] text-slate-300 hover:text-[#D4AF37] hover:bg-[#151E3D]/80 border border-slate-800"
                            }`}
                          >
                            {step.label}
                          </button>
                        ))}
                      </div>

                      {/* SVG Canvas box */}
                      <div className="relative w-full max-w-[280px] aspect-[7/4] bg-[#0E1731] border border-slate-800/80 rounded-xl overflow-hidden p-3 flex items-center justify-center shadow-inner">
                        <svg
                          key={`${numA}-${numB}-${selectedSutraStep}`}
                          viewBox="0 0 200 110"
                          className="w-full h-full overflow-visible"
                        >
                          <defs>
                            <filter id="line-glow" x="-20%" y="-20%" width="140%" height="140%">
                              <feGaussianBlur stdDeviation="3" result="blur" />
                              <feComposite in="SourceGraphic" in2="blur" operator="over" />
                            </filter>
                          </defs>

                          {/* Connection Lines (with drawing animations) */}
                          {/* Step 3 Line: Left Vertical (a1 to b1) */}
                          {(selectedSutraStep === "all" || selectedSutraStep === "step3") && (
                            <path
                              d="M 60,35 L 60,75"
                              stroke="#D4AF37"
                              strokeWidth="3.5"
                              strokeLinecap="round"
                              filter="url(#line-glow)"
                              className="sutra-line-animate"
                              style={{ animationDelay: "0s" }}
                            />
                          )}
                          {/* Step 2 Line 1: Diagonal (a1 to b0) */}
                          {(selectedSutraStep === "all" || selectedSutraStep === "step2") && (
                            <path
                              d="M 65,35 L 135,75"
                              stroke="#F59E0B"
                              strokeWidth="3.5"
                              strokeLinecap="round"
                              filter="url(#line-glow)"
                              className="sutra-line-animate"
                              style={{ animationDelay: "0.2s" }}
                            />
                          )}
                          {/* Step 2 Line 2: Diagonal (a0 to b1) */}
                          {(selectedSutraStep === "all" || selectedSutraStep === "step2") && (
                            <path
                              d="M 135,35 L 65,75"
                              stroke="#F59E0B"
                              strokeWidth="3.5"
                              strokeLinecap="round"
                              filter="url(#line-glow)"
                              className="sutra-line-animate"
                              style={{ animationDelay: "0.4s" }}
                            />
                          )}
                          {/* Step 1 Line: Right Vertical (a0 to b0) */}
                          {(selectedSutraStep === "all" || selectedSutraStep === "step1") && (
                            <path
                              d="M 140,35 L 140,75"
                              stroke="#D4AF37"
                              strokeWidth="3.5"
                              strokeLinecap="round"
                              filter="url(#line-glow)"
                              className="sutra-line-animate"
                              style={{ animationDelay: "0.6s" }}
                            />
                          )}

                          {/* Digits Display */}
                          {/* Row A: a1 a0 */}
                          <g transform="translate(0, 0)">
                            {/* a1 Box & Digit */}
                            <rect x="42" y="10" width="36" height="24" rx="8" fill="#151E3D" stroke={selectedSutraStep === "step3" ? "#D4AF37" : "rgba(212,175,55,0.25)"} strokeWidth="1.5" />
                            <text x="60" y="27" fill={selectedSutraStep === "step3" ? "#D4AF37" : "#E2E8F0"} fontSize="14" fontWeight="bold" textAnchor="middle" fontFamily="Fira Code, monospace">
                              {solverResult.a1}
                            </text>
                            
                            {/* a0 Box & Digit */}
                            <rect x="122" y="10" width="36" height="24" rx="8" fill="#151E3D" stroke={selectedSutraStep === "step1" ? "#D4AF37" : "rgba(212,175,55,0.25)"} strokeWidth="1.5" />
                            <text x="140" y="27" fill={selectedSutraStep === "step1" ? "#D4AF37" : "#E2E8F0"} fontSize="14" fontWeight="bold" textAnchor="middle" fontFamily="Fira Code, monospace">
                              {solverResult.a0}
                            </text>
                          </g>

                          {/* Row B: b1 b0 */}
                          <g transform="translate(0, 0)">
                            {/* b1 Box & Digit */}
                            <rect x="42" y="76" width="36" height="24" rx="8" fill="#151E3D" stroke={selectedSutraStep === "step3" ? "#D4AF37" : "rgba(212,175,55,0.25)"} strokeWidth="1.5" />
                            <text x="60" y="93" fill={selectedSutraStep === "step3" ? "#D4AF37" : "#E2E8F0"} fontSize="14" fontWeight="bold" textAnchor="middle" fontFamily="Fira Code, monospace">
                              {solverResult.b1}
                            </text>

                            {/* b0 Box & Digit */}
                            <rect x="122" y="76" width="36" height="24" rx="8" fill="#151E3D" stroke={selectedSutraStep === "step1" ? "#D4AF37" : "rgba(212,175,55,0.25)"} strokeWidth="1.5" />
                            <text x="140" y="93" fill={selectedSutraStep === "step1" ? "#D4AF37" : "#E2E8F0"} fontSize="14" fontWeight="bold" textAnchor="middle" fontFamily="Fira Code, monospace">
                              {solverResult.b0}
                            </text>
                          </g>

                        </svg>
                      </div>

                      {/* Helper label explanation for current mode */}
                      <div className="text-center text-xs font-sans text-slate-400 font-medium px-2 py-0.5 rounded-md bg-[#0B132B]/50 border border-slate-800/40">
                        {selectedSutraStep === "all" && "All steps visualize simultaneously."}
                        {selectedSutraStep === "step1" && `Vertically: ${solverResult.a0} × ${solverResult.b0} = ${solverResult.step1Val}`}
                        {selectedSutraStep === "step2" && `Crosswise & Add: (${solverResult.a1} × ${solverResult.b0}) + (${solverResult.a0} × ${solverResult.b1}) = ${solverResult.step2Val}`}
                        {selectedSutraStep === "step3" && `Vertically: ${solverResult.a1} × ${solverResult.b1} = ${solverResult.step3Val}`}
                      </div>
                    </div>

                    {/* Step breakdowns */}
                    <div className="space-y-3 font-equation text-sm">
                      <div className="flex items-start space-x-3 bg-[#0B132B] p-3 rounded-lg border border-slate-800">
                        <span className="bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-0.5 rounded text-xs font-bold font-mono whitespace-nowrap">Step 1</span>
                        <div className="space-y-1">
                          <p className="text-slate-300">Right column vertical product (a₀ × b₀):</p>
                          <p className="text-[#D4AF37] font-bold text-base">{solverResult.step1}</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3 bg-[#0B132B] p-3 rounded-lg border border-slate-800">
                        <span className="bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-0.5 rounded text-xs font-bold font-mono whitespace-nowrap">Step 2</span>
                        <div className="space-y-1">
                          <p className="text-slate-300">Crosswise multiplication and addition (a₁b₀ + a₀b₁):</p>
                          <p className="text-[#D4AF37] font-bold text-base">{solverResult.step2}</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3 bg-[#0B132B] p-3 rounded-lg border border-slate-800">
                        <span className="bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-0.5 rounded text-xs font-bold font-mono whitespace-nowrap">Step 3</span>
                        <div className="space-y-1">
                          <p className="text-slate-300">Left column vertical product (a₁ × b₁):</p>
                          <p className="text-[#D4AF37] font-bold text-base">{solverResult.step3}</p>
                        </div>
                      </div>
                    </div>

                    {/* Math Algebra Proof explanation */}
                    <div className="border-t border-slate-800 pt-4 space-y-1.5">
                      <div className="text-xs text-[#D4AF37] font-bold font-mono flex items-center space-x-1.5">
                        <Info className="h-3 w-3" />
                        <span>THE COMPRESSED ALGEBRAIC THEOREM:</span>
                      </div>
                      <p className="text-slate-300 font-mono text-xs overflow-x-auto bg-[#0B132B] p-2 rounded">
                        {solverResult.algebraProof}
                      </p>
                    </div>

                    {/* Final Big Answer display */}
                    <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-xl p-4 flex items-center justify-between" data-testid="vedic-solver-result-box">
                      <div>
                        <div className="text-xs text-slate-400 uppercase font-mono tracking-wider">Lightning Result</div>
                        <div className="text-2xl sm:text-3xl font-black text-[#D4AF37] font-equation mt-1">
                          {numA} × {numB} = {solverResult.finalAns}
                        </div>
                      </div>
                      <button
                        onClick={() => openBookingModalWithTier("L2 — SUTRA™ INFLUENCE (₹2499)")}
                        className="bg-[#D4AF37] hover:bg-[#FFD700] text-[#0B132B] text-xs font-bold px-4 py-2.5 rounded-lg transition-colors flex items-center space-x-1"
                        data-testid="solver-cta-btn"
                      >
                        <span>Learn More</span>
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>

                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center text-slate-400 space-y-2">
                    <Calculator className="h-10 w-10 text-slate-600 animate-pulse" />
                    <p className="text-sm font-mono">Waiting for valid two 2-digit numbers...</p>
                    <p className="text-xs text-slate-500">Please enter values between 10 and 99.</p>
                  </div>
                )}
              </div>

            </div>

          </div>
        </section>


        {/* SOLVING THE TOP 10 TARGET AUDIENCE PAIN POINTS */}
        <section id="pain-points" className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto relative">
          <div className="text-center space-y-4 mb-16">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#D4AF37]">
              Eliminating Math Phobia
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-100">
              Solving Your Top 10 Math Obstacles
            </h2>
            <p className="text-slate-300 text-sm sm:text-base max-w-2xl mx-auto">
              Our curriculum identifies, addresses, and neutralizes the ten most critical pedagogical and psychological blockages faced by learners.
            </p>
          </div>

          {/* Interactive Accordion Container */}
          <div className="space-y-4">
            {painPoints.map((pp) => {
              const isOpen = openPainPoint === pp.id;
              return (
                <div 
                  key={pp.id} 
                  className="bg-[#151E3D] border border-slate-700/60 rounded-xl overflow-hidden transition-all duration-300"
                  data-testid={`pain-point-accordion-item-${pp.id}`}
                >
                  {/* Header Trigger */}
                  <button
                    onClick={() => setOpenPainPoint(isOpen ? null : pp.id)}
                    className="w-full flex items-center justify-between p-5 text-left transition-colors hover:bg-slate-800/40"
                    data-testid={`pain-point-trigger-${pp.id}`}
                  >
                    <span className="font-display text-sm sm:text-base font-semibold text-slate-100 flex items-center pr-4">
                      <span className="text-[#D4AF37] font-mono font-bold mr-3">{String(pp.id).padStart(2, "0")}.</span>
                      {pp.title}
                    </span>
                    <ChevronDown className={`h-4.5 w-4.5 text-[#D4AF37] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  {/* Accordion Content Panel */}
                  <div 
                    className={`transition-all duration-300 ease-in-out ${
                      isOpen ? "max-h-[500px] border-t border-slate-800" : "max-h-0"
                    } overflow-hidden`}
                    data-testid={`pain-point-content-${pp.id}`}
                  >
                    <div className="p-5 sm:p-6 space-y-4 text-left text-xs sm:text-sm leading-relaxed">
                      
                      {/* Critical Problem */}
                      <div className="space-y-1">
                        <span className="text-rose-400 font-mono text-[10px] sm:text-xs uppercase tracking-wider font-semibold">The Friction Barrier:</span>
                        <p className="text-slate-400">{pp.problem}</p>
                      </div>

                      {/* Breakthrough Solution */}
                      <div className="bg-[#0B132B]/80 border border-[#D4AF37]/20 p-4 rounded-lg space-y-1">
                        <span className="text-[#D4AF37] font-mono text-[10px] sm:text-xs uppercase tracking-wider font-semibold">Vedic Breakthrough Solution:</span>
                        <p className="text-slate-200 font-medium">{pp.solution}</p>
                      </div>

                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        </section>


        {/* INSPIRATIONAL PARENT ADVICE SECTION */}
        <section className="bg-gradient-to-b from-[#151E3D]/40 to-[#0B132B] py-20 px-4 sm:px-6 lg:px-8 border-t border-slate-800 relative">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="h-10 w-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#D4AF37]/30">
              <Star className="h-5 w-5 text-[#D4AF37]" />
            </div>
            
            <h2 className="font-display text-2xl sm:text-4xl text-slate-100 font-bold leading-snug">
              Parents: Don't Just Ask, &ldquo;Which Vedic Maths Digital Coaching Should My Child Join?&rdquo;
            </h2>

            <p className="text-[#D4AF37] font-display text-lg sm:text-xl font-semibold uppercase tracking-wide">
              Ask: &ldquo;What kind of mathematical genius should my child become over the years?&rdquo;
            </p>

            <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
              Our unique framework is built systematically on the foundational pillars of 
              <strong> Speed, Understanding, Transformation, Resilience, Accuracy, Clarity, Confidence, Collaboration, Wisdom, and Joyfulness</strong>. 
              These cognitive capabilities are invaluable keys to absolute academic success.
            </p>

            <button
              onClick={() => openBookingModalWithTier("L2 — SUTRA™ INFLUENCE (₹2499)")}
              className="bg-[#D4AF37] hover:bg-[#FFD700] text-[#0B132B] font-bold text-xs sm:text-sm px-6 sm:px-8 py-3 rounded-xl transition-transform transform hover:scale-105 active:scale-95 shadow-[0_4px_15px_rgba(212,175,55,0.25)]"
              data-testid="parent-section-cta"
            >
              Secure Your Child's Spot Now
            </button>
          </div>
        </section>


        {/* THE FOUNDER & MASTER TRAINER IDENTITY */}
        <section id="mentor" className="py-20 sm:py-28 bg-[#151E3D]/50 border-y border-slate-800/80 px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
              
              {/* Left Bio Column */}
              <div className="lg:col-span-7 space-y-6 text-left">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                  Founder & Master Trainer
                </span>
                
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-100">
                  Vijay Kumar Dharmadhikari
                </h2>
                
                <p className="text-slate-400 font-mono text-xs sm:text-sm tracking-wide">
                  BE (Mechanical Engineering) • MBA (USA) • PhD (Management), New Delhi
                </p>

                <div className="h-0.5 w-16 bg-[#D4AF37]"></div>

                <div className="space-y-4 text-slate-300 text-sm sm:text-base leading-relaxed">
                  <p>
                    With over 35 years of experience in the industry, Vijay Dharmadhikari takes forward the authentic Vedic Mathematics teachings of Parampujya Jagadguru Shankaracharya Swami Shri Bharati Krishna Tirthaji.
                  </p>
                  <p>
                    For over 7 years, Vijay Dharmadhikari taught Vedic Mathematics in <strong>Aryabhata Vignana Parishad</strong>—a highly respected organization focused entirely on nurturing scientific temper among young minds.
                  </p>
                </div>

                {/* Direct Inspiring Quote */}
                <div className="bg-[#0B132B]/75 border-l-2 border-[#D4AF37] p-5 rounded-r-xl italic text-slate-200 text-sm sm:text-base leading-relaxed">
                  &ldquo;My purpose is not merely to tell young men and women how to answer Vedic Mathematics questions. It is to help them start developing into the kind of young men and women who can fearlessly face mathematical problems with joy, clarity, confidence, accuracy, and speed.&rdquo;
                </div>

                {/* Social Connect Profiles - STRICTLY MOCKED ACCORDING TO BIO INFO */}
                <div className="pt-4 flex items-center space-x-4">
                  <span className="text-xs text-slate-400 font-mono uppercase">Connect:</span>
                  <a 
                    href="https://facebook.com/dvkumar72026" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="h-10 w-10 bg-[#0B132B] border border-slate-700 rounded-full flex items-center justify-center hover:text-[#D4AF37] hover:border-[#D4AF37] transition-all text-slate-300"
                    title="Connect on Facebook"
                    data-testid="mentor-facebook-link"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a 
                    href="https://linkedin.com/in/vijay-kumar-dharmadhikari" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="h-10 w-10 bg-[#0B132B] border border-slate-700 rounded-full flex items-center justify-center hover:text-[#D4AF37] hover:border-[#D4AF37] transition-all text-slate-300"
                    title="Connect on LinkedIn"
                    data-testid="mentor-linkedin-link"
                  >
                    <Linkedin className="h-5 w-5" />
                  </a>
                </div>

              </div>

              {/* Right Photo Column */}
              <div className="lg:col-span-5 flex justify-center">
                <div className="relative w-full max-w-[380px]">
                  {/* Subtle golden background box frame */}
                  <div className="absolute top-4 -left-4 -bottom-4 right-4 border border-[#D4AF37]/30 rounded-2xl"></div>
                  
                  <div className="relative bg-[#151E3D] border border-slate-700 rounded-2xl overflow-hidden shadow-xl p-3">
                    <img 
                      src={VIJAY_PHOTO_URL} 
                      alt="Vijay Kumar Dharmadhikari Master Trainer Photo" 
                      className="w-full rounded-xl object-cover aspect-[4/5] object-top"
                      data-testid="mentor-profile-photo"
                    />
                    <div className="mt-3 text-center py-1">
                      <p className="text-slate-200 font-display text-sm font-semibold tracking-wider">VIJAY KUMAR DHARMADHIKARI</p>
                      <p className="text-xs text-slate-400 mt-0.5">Vedic Maths Master Trainer & Founder</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>


        {/* THE VEDIC MATHS MASTERY CIRCLE COMMUNITY PLATFORM */}
        <section className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Photo Column */}
            <div className="lg:col-span-5 order-2 lg:order-1 flex justify-center">
              <div className="relative w-full max-w-[420px]">
                <div className="absolute -inset-1.5 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-3xl blur opacity-25"></div>
                <div className="relative bg-[#151E3D] border border-slate-700/60 rounded-3xl p-3 shadow-2xl">
                  <img 
                    src={MASTERY_CIRCLE_URL} 
                    alt="The Vedic Maths Mastery Circle Community Platform Thumbnail" 
                    className="w-full rounded-2xl object-cover aspect-[4/3] sm:aspect-[16/10]"
                    data-testid="community-circle-thumbnail"
                  />
                </div>
              </div>
            </div>

            {/* Right Copy Column */}
            <div className="lg:col-span-7 order-1 lg:order-2 space-y-6 text-left">
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#D4AF37] font-semibold bg-[#D4AF37]/10 px-3 py-1 rounded-full">
                Global Circle Platform
              </span>
              
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-100">
                Join &ldquo;The Vedic Maths Mastery Circle&rdquo;
              </h2>

              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Specifically built for certified educators, teachers, and passionate math learners. Our overarching community circles foster lifelong mentorship, peer problem quests, pedagogy delivery, and training certification.
              </p>

              <div className="space-y-4">
                <div className="flex items-start space-x-3 text-sm">
                  <div className="mt-1 h-5 w-5 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-200">Standardized TTT Certifications</h3>
                    <p className="text-slate-400 text-xs sm:text-sm mt-0.5">Scale classrooms using professional experiential math teaching methods.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 text-sm">
                  <div className="mt-1 h-5 w-5 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                    <Check className="h-3 w-3 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-200">Global Peer Mathematics Tournaments</h3>
                    <p className="text-slate-400 text-xs sm:text-sm mt-0.5">Let learners collaborate, compete, and enjoy numbers together.</p>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <button
                  onClick={() => openBookingModalWithTier("L3 — SUTRA™ WISDOM™ (₹4999)")}
                  className="bg-[#D4AF37] hover:bg-[#FFD700] text-[#0B132B] font-bold text-xs sm:text-sm px-6 py-3 rounded-xl transition-all shadow-lg"
                  data-testid="community-section-cta"
                >
                  Join Our Global Master Circle
                </button>
              </div>

            </div>

          </div>
        </section>


        {/* COURSE CURRICULUM AND MEMBERSHIP TIERS SECTION */}
        <section id="curriculum" className="py-20 sm:py-28 bg-[#151E3D]/30 border-t border-slate-800 px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-7xl mx-auto">
            
            <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#D4AF37]">
                Membership Syllabi
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-100">
                Structure of the Courses
              </h2>
              <p className="text-slate-300 text-sm sm:text-base">
                Discover our specialized structured tiers. Step into your custom speed calculation journey today.
              </p>
            </div>

            {/* Curriculum cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
              {membershipTiers.map((tier) => (
                <div 
                  key={tier.id}
                  className={`bg-[#151E3D] border rounded-2xl p-6 text-left flex flex-col justify-between transition-all duration-300 hover:-translate-y-1 shadow-xl relative ${tier.color}`}
                  data-testid={`tier-card-${tier.id}`}
                >
                  {/* Popular badge */}
                  {tier.id === "l2" && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0B132B] font-mono text-[10px] sm:text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full shadow-[0_0_12px_rgba(212,175,55,0.4)]">
                      Most Popular
                    </div>
                  )}

                  <div className="space-y-4">
                    
                    {/* Header tier details */}
                    <div>
                      <span className="text-[#D4AF37] font-mono text-xs font-bold uppercase tracking-wider">{tier.badge}</span>
                      <h3 className="font-display text-lg font-bold text-slate-100 leading-tight mt-1">
                        {tier.name}
                      </h3>
                      <p className="text-slate-400 text-xs mt-1 italic font-sans leading-none">{tier.tagline}</p>
                    </div>

                    {/* Price and strikeout */}
                    <div className="border-y border-slate-700/50 py-3.5">
                      <div className="flex items-baseline space-x-1.5">
                        <span className="text-3xl font-black text-slate-100 font-equation">₹{tier.price}</span>
                        <span className="text-slate-500 font-mono text-sm line-through">{tier.originalPrice}</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono tracking-wider mt-0.5 block uppercase">ONE-TIME LIFE ACCESS</span>
                    </div>

                    {/* Feature list bullet points */}
                    <div className="space-y-2.5 pt-1.5">
                      <p className="text-xs text-slate-400 font-mono uppercase tracking-wider font-semibold">Included Syllabus:</p>
                      <ul className="space-y-2 text-xs sm:text-sm text-slate-300 font-sans">
                        {tier.features.map((feat, fi) => (
                          <li key={fi} className="flex items-start">
                            <Check className="h-4 w-4 text-[#D4AF37] mr-1.5 shrink-0 mt-0.5" />
                            <span className="leading-tight">{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                  </div>

                  {/* Button call trigger */}
                  <div className="pt-6 mt-6 border-t border-slate-800">
                    <button
                      onClick={() => openBookingModalWithTier(`${tier.id === "l0" ? "L0 — SUTRA IDENTITY (₹499)" : tier.id === "l1" ? "L1 — SUTRA™ ALIGNMENT (₹999)" : tier.id === "l2" ? "L2 — SUTRA™ INFLUENCE (₹2499)" : "L3 — SUTRA™ WISDOM™ (₹4999)"}`)}
                      className={`w-full py-3 rounded-xl text-xs sm:text-sm font-bold tracking-wide transition-all ${
                        tier.id === "l2"
                          ? "bg-[#D4AF37] hover:bg-[#FFD700] text-[#0B132B] shadow-[0_4px_15px_rgba(212,175,55,0.3)]"
                          : "border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/5"
                      }`}
                      data-testid={`tier-select-${tier.id}-btn`}
                    >
                      Book My Seat Now
                    </button>
                  </div>

                </div>
              ))}
            </div>

          </div>
        </section>


        {/* FINAL HERO CALL TO ACTION BANNER */}
        <section className="py-24 px-4 sm:px-6 lg:px-8 max-5xl mx-auto relative">
          <div className="bg-[#151E3D] border border-[#D4AF37]/35 rounded-3xl p-8 sm:p-12 text-center space-y-8 shadow-2xl relative overflow-hidden">
            
            {/* Background design elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#D4AF37]/5 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#D4AF37]/5 rounded-full blur-2xl"></div>

            <div className="max-w-2xl mx-auto space-y-4">
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#D4AF37] font-semibold bg-[#D4AF37]/10 px-3.5 py-1.5 rounded-full">
                Start Your Vedic Journey Early
              </span>
              <h2 className="font-display text-3xl sm:text-5xl font-bold text-slate-100 leading-tight">
                Unlock Your Inner Genius Today
              </h2>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Know your SUTRA™ path. Build a rock-solid mathematical foundation. Conquer competitive exams with absolute speed and joy.
              </p>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row justify-center items-center gap-4">
              <button
                onClick={() => openBookingModalWithTier("L2 — SUTRA™ INFLUENCE (₹2499)")}
                className="bg-[#D4AF37] hover:bg-[#FFD700] text-[#0B132B] font-bold text-base px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-[0_4px_20px_rgba(212,175,55,0.35)] flex items-center space-x-2"
                data-testid="bottom-cta-book-seat"
              >
                <span>Book My Seat (Join The Circle)</span>
                <Sparkles className="h-5 w-5" />
              </button>
            </div>

            <p className="text-xs text-slate-400 font-mono uppercase tracking-wider">
              One-Time Payment • Lifetime Circle Community Mentorship Included
            </p>

          </div>
        </section>

      </main>


      {/* FOOTER */}
      <footer className="bg-[#080E1E] border-t border-slate-800 text-left relative z-10 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 items-center border-b border-slate-800/80 pb-8">
          
          {/* Logo brand */}
          <div className="md:col-span-5 space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src={LOGO_URL} 
                alt="The Vedic Maths Mastery Hub Logo" 
                className="h-10 w-10 object-contain"
                data-testid="footer-logo"
              />
              <div className="flex flex-col text-left">
                <span className="font-display text-[#D4AF37] font-bold text-sm tracking-wider leading-none">
                  THE VEDIC MATHS MASTERY HUB
                </span>
                <span className="text-[10px] text-slate-400 font-mono tracking-widest leading-none mt-1">
                  ESTD 2026 • VIJAY DHARMADHIKARI
                </span>
              </div>
            </div>
            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed max-w-md">
              Rooted in the brilliance of ancient Indian traditions and elevated by modern experiential pedagogy. Building a global legacy of certified educators.
            </p>
          </div>

          {/* Quick legal / details block */}
          <div className="md:col-span-4 space-y-2 text-slate-400 text-xs sm:text-sm">
            <p className="font-semibold text-slate-200 uppercase font-mono tracking-wider">The Vedic Maths Mastery Circle</p>
            <p>Community and collaboration framework transforming dreams to reality.</p>
            <p>Canonical domain: <a href="https://learn.thevedicmathsmasteryhub.com" className="text-[#D4AF37] underline" data-testid="footer-domain-link">learn.thevedicmathsmasteryhub.com</a></p>
          </div>

          {/* Contact info block */}
          <div className="md:col-span-3 space-y-2 text-slate-400 text-xs sm:text-sm">
            <p className="font-semibold text-slate-200 uppercase font-mono tracking-wider">Connect Details</p>
            <p className="flex items-center"><Mail className="h-3.5 w-3.5 mr-1.5 text-[#D4AF37]" /> <a href="mailto:dvkumar7@gmail.com" className="hover:text-[#D4AF37] hover:underline" data-testid="footer-email-link">dvkumar7@gmail.com</a></p>
            <p className="flex items-center"><Facebook className="h-3.5 w-3.5 mr-1.5 text-[#D4AF37]" /> dvkumar72026/facebook</p>
            <p className="flex items-center"><Linkedin className="h-3.5 w-3.5 mr-1.5 text-[#D4AF37]" /> Vijay Kumar Dharmadhikari</p>
          </div>

        </div>

        {/* Legal copyrights */}
        <div className="max-w-7xl mx-auto pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500">
          <p>&copy; 2026 The Vedic Maths Mastery Hub. All rights reserved. Created for learners, educators, and certified math mentors.</p>
          <p className="mt-2 sm:mt-0 font-mono uppercase text-[10px] tracking-widest">Built with ancient wisdom for modern success</p>
        </div>
      </footer>


      {/* INTERACTIVE LEAD CAPTURE DIALOG / MODAL */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={handleOverlayClick}
          data-testid="booking-modal-overlay"
        >
          {/* Modal Container Card */}
          <div 
            className="relative bg-[#151E3D] border border-[#D4AF37]/40 w-full max-w-lg rounded-2xl shadow-2xl p-6 sm:p-8 text-left animate-in zoom-in-95 duration-200"
            role="dialog"
            aria-modal="true"
            data-testid="booking-modal-content"
          >
            
            {/* Close trigger button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-100 transition-colors focus:outline-none"
              aria-label="Close dialog"
              data-testid="booking-modal-close-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Body */}
            {!formSuccess ? (
              <div className="space-y-6">
                
                {/* Header title */}
                <div className="space-y-1.5">
                  <span className="text-[#D4AF37] font-mono text-xs uppercase tracking-wider font-semibold block">Securing Your Hub Seat</span>
                  <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-100">
                    Book My Seat
                  </h3>
                  <p className="text-slate-400 text-xs sm:text-sm">
                    Enter your contact details below to join the community circle. Currently enrolling for: <strong className="text-[#D4AF37]">{selectedTier}</strong>
                  </p>
                </div>

                {/* Form Elements */}
                <form onSubmit={handleLeadSubmit} className="space-y-4">
                  
                  {/* Name field */}
                  <div className="space-y-1">
                    <label className="text-xs text-slate-300 font-medium block text-left">Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        placeholder="Vijay Kumar"
                        className="w-full bg-[#0B132B] border border-slate-700 focus:border-[#D4AF37] text-slate-100 text-xs sm:text-sm pl-10 pr-4 py-2.5 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                        data-testid="lead-form-name-input"
                      />
                    </div>
                  </div>

                  {/* Email field */}
                  <div className="space-y-1">
                    <label className="text-xs text-slate-300 font-medium block text-left">Email Address *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="vijay@example.com"
                        className="w-full bg-[#0B132B] border border-slate-700 focus:border-[#D4AF37] text-slate-100 text-xs sm:text-sm pl-10 pr-4 py-2.5 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                        data-testid="lead-form-email-input"
                      />
                    </div>
                  </div>

                  {/* Phone field */}
                  <div className="space-y-1">
                    <label className="text-xs text-slate-300 font-medium block text-left">Phone Number *</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+91 98765 43210"
                        className="w-full bg-[#0B132B] border border-slate-700 focus:border-[#D4AF37] text-slate-100 text-xs sm:text-sm pl-10 pr-4 py-2.5 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                        data-testid="lead-form-phone-input"
                      />
                    </div>
                  </div>

                  {/* Dynamic Tier Selector inside the Modal */}
                  <div className="space-y-1">
                    <label className="text-xs text-slate-300 font-medium block text-left">Selected Membership Level *</label>
                    <select
                      value={selectedTier}
                      onChange={(e) => setSelectedTier(e.target.value)}
                      className="w-full bg-[#0B132B] border border-slate-700 focus:border-[#D4AF37] text-slate-100 text-xs sm:text-sm px-3 py-2.5 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                      data-testid="lead-form-tier-select"
                    >
                      <option value="L0 — SUTRA IDENTITY (₹499)">L0 — SUTRA IDENTITY (₹499)</option>
                      <option value="L1 — SUTRA™ ALIGNMENT (₹999)">L1 — SUTRA™ ALIGNMENT (₹999)</option>
                      <option value="L2 — SUTRA™ INFLUENCE (₹2499)">L2 — SUTRA™ INFLUENCE (₹2499)</option>
                      <option value="L3 — SUTRA™ WISDOM™ (₹4999)">L3 — SUTRA™ WISDOM™ (₹4999)</option>
                    </select>
                  </div>

                  {/* Audience type field */}
                  <div className="space-y-1">
                    <label className="text-xs text-slate-300 font-medium block text-left">Who are you? *</label>
                    <select
                      value={formData.audienceType}
                      onChange={(e) => setFormData({ ...formData, audienceType: e.target.value })}
                      className="w-full bg-[#0B132B] border border-slate-700 focus:border-[#D4AF37] text-slate-100 text-xs sm:text-sm px-3 py-2.5 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                      data-testid="lead-form-audience-select"
                    >
                      <option value="Student">Student (Classes 4th to 12th)</option>
                      <option value="Parent">Parent / Guardian</option>
                      <option value="Teacher">Teacher / School Educator</option>
                      <option value="Entrepreneur">Entrepreneur / Solopreneur</option>
                      <option value="Learner">Lifelong Math Enthusiast</option>
                    </select>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      className="w-full bg-[#D4AF37] hover:bg-[#FFD700] text-[#0B132B] font-bold py-3 rounded-xl transition-all shadow-md text-xs sm:text-sm"
                      data-testid="lead-form-submit-btn"
                    >
                      Reserve My Seat & Join Circle
                    </button>
                  </div>

                  <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                    By reserving your seat, you agree to receive follow-up emails and details regarding your chosen Vedic Mathematical tier. Your data is 100% confidential and processed internally in accordance with IKS initiatives.
                  </p>

                </form>

              </div>
            ) : (
              // Success celebratory confirmation state
              <div className="text-center py-6 space-y-6 animate-in fade-in duration-300" data-testid="lead-success-card">
                
                {/* Sparkly Check Icons */}
                <div className="h-16 w-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto border border-[#D4AF37]">
                  <Sparkles className="h-8 w-8 text-[#D4AF37]" />
                </div>

                <div className="space-y-2">
                  <h3 className="font-display text-2xl font-black text-slate-100">
                    Seat Reserved!
                  </h3>
                  <p className="text-[#D4AF37] font-mono text-xs font-bold uppercase tracking-wider">
                    {selectedTier}
                  </p>
                  <p className="text-slate-300 text-sm max-w-sm mx-auto leading-relaxed">
                    Thank you <strong className="text-slate-100">{formData.fullName}</strong>. Your seat is provisionally reserved. A confirmation will be sent to <strong className="text-slate-100">{formData.email}</strong>.
                  </p>
                </div>

                {/* Booking confirmation code card */}
                <div className="bg-[#0B132B] border border-[#D4AF37]/20 p-4 rounded-xl max-w-xs mx-auto">
                  <span className="text-[10px] text-slate-400 font-mono tracking-wider block uppercase">Registration Code</span>
                  <span className="text-xl font-bold text-[#D4AF37] font-equation block tracking-wider mt-1">{submissionCode}</span>
                </div>

                <p className="text-slate-500 text-[11px] italic">
                  This site is currently in live-lead preview mode inside TagMango Studio.
                </p>

                {/* Action button */}
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="bg-[#D4AF37] hover:bg-[#FFD700] text-[#0B132B] text-xs font-bold px-6 py-2.5 rounded-xl transition-colors"
                  data-testid="success-close-btn"
                >
                  Return to Landing Page
                </button>

              </div>
            )}

          </div>
        </div>
      )}

      {/* FIXED TOAST NOTIFICATION CONTAINER */}
      {showToast && (
        <div 
          className="fixed bottom-5 right-5 z-[100] bg-[#151E3D] border border-[#D4AF37] p-4 rounded-xl shadow-2xl flex items-center space-x-3 max-w-sm animate-in slide-in-from-bottom duration-300"
          data-testid="toast-container"
        >
          <div className="h-8 w-8 bg-[#D4AF37]/10 rounded-full flex items-center justify-center shrink-0">
            <Sparkles className="h-4.5 w-4.5 text-[#D4AF37]" />
          </div>
          <div className="text-left">
            <span className="text-xs text-slate-400 font-mono block">Vedic Maths Mastery Hub</span>
            <p className="text-sm font-semibold text-slate-100 mt-0.5 leading-snug">{toastMessage}</p>
          </div>
        </div>
      )}

    </div>
  );
}
